<script setup>
import { ref, onMounted, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Spinner from '../../../../../components-next/spinner/Spinner.vue';
import Button from '../../../../../components-next/button/Button.vue';
import ValidationFeedback from '../shared/ValidationFeedback.vue';

const { t } = useI18n();
const store = useStore();
const emit = defineEmits(['select']);

const props = defineProps({
  initialFlowId: {
    type: Number,
    default: null
  },
  // Nova prop para detectar mudança de passo
  currentStep: {
    type: Number,
    default: null
  }
});

const conversationFlows = [
  {
    id: 1,
    name: 'Qualificação',
    description: 'Fluxo inicial para coletar e validar todas as informações do cliente antes de qualquer recomendação.',
    features: [
      'Saudação e apresentação do agente IA',
      'Coleta de dados do cliente',
      'Perguntas direcionadas para mapear interesses e necessidades',
      'Validação e confirmação das informações fornecidas'
    ],
    flowSteps: [
      { step: 1, action: 'Saudação', example: 'Olá! Sou Sofia da TechCorp. Para começar, poderia me informar seu nome?' },
      { step: 2, action: 'Coleta de Dados', example: 'Ótimo, João! Agora preciso de seu email e telefone para contato.' },
      { step: 3, action: 'Identificação de Necessidade', example: 'Perfeito! Me conte, o que te traz à TechCorp hoje?' },
      { step: 4, action: 'Qualificação', example: 'Entendi. Para te ajudar melhor, qual é o tamanho da sua empresa?' },
      { step: 5, action: 'Direcionamento', example: 'Baseado no que conversamos, vou te conectar com nosso especialista em soluções empresariais.' }
    ]
  },
  {
    id: 2,
    name: 'Vendas',
    description: 'Fluxo focado em entender estilo e preferências para oferecer produtos personalizados e conduzir até a compra.',
    features: [
      'Análise de estilo e preferências do cliente',
      'Sugestões personalizadas baseadas no perfil',
      'Apresentação de produtos e coleções',
      'Assistência com tamanhos, cores e modelos'
    ],
    flowSteps: [
      { step: 1, action: 'Saudação', example: 'Olá! Bem-vindo à Fashion Style! Sou a Luna, sua consultora virtual. Qual seu nome?' },
      { step: 2, action: 'Descoberta de Estilo', example: 'Que tipo de look você está buscando hoje? Casual, social, esportivo?' },
      { step: 3, action: 'Apresentação', example: 'Perfeito! Temos peças incríveis que combinam com seu estilo. Vou te mostrar nossa nova coleção...' },
      { step: 4, action: 'Assistência', example: 'Gostou dessa blusa? Ela está disponível nos tamanhos P, M e G, e temos nas cores azul, branco e rosa.' },
      { step: 5, action: 'Finalização', example: 'Ótima escolha! Vou adicionar ao seu carrinho. Gostaria de ver algum acessório para combinar?' },
      { step: 6, action: 'Checkout', example: 'Tudo pronto! Frete grátis para sua região. Vou te direcionar para finalizar a compra!' }
    ]
  },
  {
    id: 3,
    name: 'Agendamento',
    description: 'Fluxo de agendamento de serviços ou atendimentos e vendas de produtos',
    features: [
      'Verificação automática de disponibilidade de horários',
      'Marcação de data e hora com confirmação imediata',
      'Envio de lembretes e possibilidade de reagendamento',
      'Recomendação de produtos ou pacotes adicionais'
    ],
    flowSteps: [
      { step: 1, action: 'Saudação', example: 'Olá! Bem-vindo à Clínica DentMax! Sou a assistente virtual Dra. Clara. Qual seu nome?' },
      { step: 2, action: 'Identificação do Problema', example: 'Como posso te ajudar hoje? É para limpeza, dor de dente, check-up ou algum procedimento específico?' },
      { step: 3, action: 'Especialista', example: 'Para canal, vou agendar com nosso endodontista Dr. Santos. Ele atende terças e quintas.' },
      { step: 4, action: 'Horário', example: 'Temos disponível: terça às 14h30 ou quinta às 10h15. Qual prefere?' },
      { step: 5, action: 'Confirmação', example: 'Agendado! Terça, 14h30 com Dr. Santos. Evite comer 2h antes da consulta.' },
      { step: 6, action: 'Orientações', example: 'Confirmação enviada por WhatsApp! Lembre-se de trazer documento e cartão do convênio.' }
    ]
  }
];

const selectedFlowId = ref(props.initialFlowId);
const error = ref(null);
const showFlowDemo = ref(false);
const demoFlow = ref(null);
const currentDemoStep = ref(0);
const hasInteracted = ref(false);

// Array para armazenar todos os intervals/timeouts ativos
const activeTimers = ref([]);

// Função para limpar todos os timers
const clearAllTimers = () => {
  activeTimers.value.forEach(timer => {
    if (timer.type === 'interval') {
      clearInterval(timer.id);
    } else {
      clearTimeout(timer.id);
    }
  });
  activeTimers.value = [];
};

// Função para resetar o estado da demonstração
const resetDemoState = () => {
  clearAllTimers();
  showFlowDemo.value = false;
  demoFlow.value = null;
  currentDemoStep.value = 0;
};

// Função para fechar demonstração (usada pelo botão X)
const closeFlowDemo = () => {
  resetDemoState();
};

// Função para iniciar demonstração do fluxo
const startFlowDemo = (flow) => {
  // Limpa timers anteriores sempre
  clearAllTimers();
  
  if (demoFlow.value?.id === flow.id && showFlowDemo.value) {
    // Se está fechando a mesma demonstração
    resetDemoState();
  } else {
    // Se está abrindo uma demonstração (nova ou mesma)
    demoFlow.value = flow;
    showFlowDemo.value = true;
    currentDemoStep.value = 0;
    
    // Pequeno delay antes de iniciar a animação
    const startTimeout = setTimeout(() => {
      startDemoAnimation();
    }, 100);
    
    activeTimers.value.push({ type: 'timeout', id: startTimeout });
  }
};

// Função para iniciar a animação da demonstração
const startDemoAnimation = () => {
  if (!showFlowDemo.value || !demoFlow.value) return;
  
  // Avançar automaticamente pelos passos
  const interval = setInterval(() => {
    // Verifica se a demonstração ainda está aberta
    if (!showFlowDemo.value) {
      clearInterval(interval);
      return;
    }
    
    if (currentDemoStep.value < demoFlow.value.flowSteps.length - 1) {
      currentDemoStep.value++;
    } else {
      // Para o intervalo mas mantém a demonstração aberta
      clearInterval(interval);
      // Remove o interval da lista de timers ativos
      activeTimers.value = activeTimers.value.filter(timer => timer.id !== interval);
    }
  }, 1500); // 1.5 segundos por passo
  
  activeTimers.value.push({ type: 'interval', id: interval });
};

const selectFlow = (id) => {
  selectedFlowId.value = id;
  hasInteracted.value = true;
  emit('select', id);
};

// Observa mudanças na prop initialFlowId
watch(() => props.initialFlowId, (newId) => {
  if (newId !== selectedFlowId.value) {
    selectedFlowId.value = newId;
  }
});

// Observa mudanças no currentStep para resetar a demonstração
watch(() => props.currentStep, () => {
  resetDemoState();
});

// Se já houver um fluxo selecionado na montagem, marca como interagido
onMounted(() => {
  if (props.initialFlowId) {
    hasInteracted.value = true;
  }
});

// Limpa todos os timers quando o componente for destruído
onBeforeUnmount(() => {
  clearAllTimers();
});

// Expõe o método de reset para o componente pai poder chamar se necessário
defineExpose({
  resetDemoState
});
</script>

<template>
  <div>
    <!-- Mensagem de erro quando nenhum fluxo está selecionado -->
    <ValidationFeedback 
      :error="!selectedFlowId && hasInteracted ? 'Por favor, selecione um fluxo de conversa para o agente' : ''"
      :touched="hasInteracted"
      :show-helper="false"
      class="mb-4"
    />

    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div 
        v-for="flow in conversationFlows" 
        :key="flow.id"
        class="bg-n-solid-2 rounded-2xl shadow-sm cursor-pointer hover:shadow-md transition-all duration-200 border-2"
        :class="{ 
          'border-n-brand': selectedFlowId === flow.id,
          'border-n-slate-3': selectedFlowId !== flow.id 
        }"
        @click="selectFlow(flow.id)"
      >
        <div class="flex flex-col h-full px-6 py-5">
          <div class="flex flex-col p-4 h-full">
            <div class="flex items-center justify-between mb-2">
              <h4 class="text-lg font-medium text-n-slate-12">{{ flow.name }}</h4>
              <div v-if="selectedFlowId === flow.id"
                class="rounded-full bg-n-brand text-white p-1 w-6 h-6 flex items-center justify-center">
                <span class="i-lucide-check text-sm" />
              </div>
            </div>

            <p class="text-sm text-n-slate-11 mb-4 flex-grow">{{ flow.description }}</p>

            <div class="pt-2 border-t border-n-slate-3">
              <h5 class="text-sm font-medium mb-2 text-n-slate-12">Principais Características:</h5>
              <ul class="space-y-1">
                <li v-for="(feature, idx) in flow.features" :key="idx" class="text-sm text-n-slate-11 flex items-start">
                  <span class="i-lucide-check text-n-brand mr-2 flex-shrink-0 mt-1" />
                  <span>{{ feature }}</span>
                </li>
              </ul>
            </div>
            
            <!-- Botão de Demonstração -->
            <div class="flex-grow"></div>
            <Button
              size="sm"
              variant="outline"
              color="slate"
              :label="demoFlow?.id === flow.id && showFlowDemo ? 'Parar Demonstração' : 'Ver Demonstração'"
              :icon="demoFlow?.id === flow.id && showFlowDemo ? 'i-lucide-pause' : 'i-lucide-play'"
              class="mt-3 w-full"
              @click.stop="startFlowDemo(flow)"
            />
          </div>
        </div>
      </div>
    </div>
    
    <!-- Demonstração Interativa do Fluxo -->
    <div v-if="showFlowDemo && demoFlow" 
      class="mt-6 bg-n-slate-2 rounded-2xl p-6 border border-n-slate-3">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-medium text-n-slate-12">
          Demonstração: Fluxo de {{ demoFlow.name }}
        </h3>
        <Button
          size="sm"
          variant="ghost"
          color="slate"
          icon="i-lucide-x"
          @click="closeFlowDemo"
        />
      </div>
      
      <!-- Progressão do Fluxo -->
      <div class="mb-6">
        <div class="flex items-center justify-between mb-2">
          <span class="text-sm text-n-slate-11">Progresso do Fluxo</span>
          <span class="text-sm text-n-slate-11">{{ currentDemoStep + 1 }} / {{ demoFlow.flowSteps.length }}</span>
        </div>
        <div class="w-full bg-n-slate-3 rounded-full h-2">
          <div 
            class="bg-n-brand h-2 rounded-full transition-all duration-1000"
            :style="{ width: `${((currentDemoStep + 1) / demoFlow.flowSteps.length) * 100}%` }"
          />
        </div>
      </div>
      
      <!-- Visualização dos Passos -->
      <div class="space-y-4">
        <div 
          v-for="(step, index) in demoFlow.flowSteps"
          :key="step.step"
          class="transition-all duration-300"
          :class="{
            'opacity-100': index <= currentDemoStep,
            'opacity-30': index > currentDemoStep
          }"
        >
          <div class="flex items-start gap-4">
            <!-- Ícone do Passo -->
            <div 
              class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300"
              :class="{
                'bg-n-brand text-white': index <= currentDemoStep,
                'bg-n-slate-3 text-n-slate-11': index > currentDemoStep
              }"
            >
              <span v-if="index < currentDemoStep" class="i-lucide-check" />
              <span v-else>{{ step.step }}</span>
            </div>
            
            <!-- Conteúdo do Passo -->
            <div class="flex-1">
              <h4 class="font-medium text-n-slate-12 mb-1">{{ step.action }}</h4>
              <div 
                v-if="index <= currentDemoStep"
                class="bg-white dark:bg-slate-800 rounded-lg p-3 text-sm text-n-slate-11 border border-n-slate-3 animate-fade-in"
              >
                <div class="flex items-start gap-2">
                  <span class="text-n-brand font-medium">Agente:</span>
                  <span>{{ step.example }}</span>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Linha de Conexão -->
          <div 
            v-if="index < demoFlow.flowSteps.length - 1"
            class="ml-5 h-4 w-0.5 bg-n-slate-3"
          />
        </div>
      </div>
      
      <!-- Status de Seleção -->
      <div v-if="selectedFlowId === demoFlow.id" class="mt-6 pt-4 border-t border-n-slate-3">
        <div class="text-center text-sm text-green-600 dark:text-green-400 font-medium">
          ✓ Fluxo Selecionado
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in {
  animation: fade-in 0.3s ease-out;
}
</style>